import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Segmented,
  Select,
  Tag,
  Typography,
  message as antdMessage,
} from "antd";
import { MessageOutlined, SearchOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { collection, getDocs } from "firebase/firestore";
import { useSearchParams } from "react-router-dom";
import { auth, db } from "../../firebase";
import {
  createGroup,
  isThreadUnread,
  subscribeMyThreadReads,
  subscribeMyThreads,
} from "../../helper/discussions";
import { getInitials, getUserLabel } from "../../helper/userDisplay";
import PageHeader from "../../components/pageHeader/PageHeader";
import DiscussionDrawer from "../../components/DiscussionDrawer";
import "./messages.css";

dayjs.extend(relativeTime);

const SUBJECT_TAG = { task: "Task", inspection: "Inspection", property: "Property" };

// Admin Messages: ALL of the admin's conversations — 1:1 DMs, standalone group
// chats, plus the task/inspection/property discussion threads they're a
// participant in — most-recently active first, with unread highlighting. "New
// conversation" starts a DM or creates a group; clicking a row opens that thread
// in the shared DiscussionDrawer.
const Messages = () => {
  const myUid = auth.currentUser?.uid || null;
  const [threads, setThreads] = useState([]);
  const [reads, setReads] = useState({});
  const [users, setUsers] = useState([]);
  const [openThread, setOpenThread] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkThread = searchParams.get("thread");

  // New-conversation modal state.
  const [newOpen, setNewOpen] = useState(false);
  const [newKind, setNewKind] = useState("dm");
  const [dmUid, setDmUid] = useState(null);
  const [groupUids, setGroupUids] = useState([]);
  const [groupTitle, setGroupTitle] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!myUid) return undefined;
    const unsub = subscribeMyThreads(myUid, setThreads);
    return unsub;
  }, [myUid]);

  useEffect(() => {
    if (!myUid) return undefined;
    const unsub = subscribeMyThreadReads(myUid, setReads);
    return unsub;
  }, [myUid]);

  useEffect(() => {
    getDocs(collection(db, "users"))
      .then((snap) => setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
      .catch((e) => console.error("messages: users load failed", e?.message));
  }, []);

  const usersById = useMemo(() => {
    const m = {};
    users.forEach((u) => {
      m[u.id] = u;
    });
    return m;
  }, [users]);

  const rows = useMemo(
    () =>
      threads.map((t) => {
        const isDmRow = t.kind === "dm";
        const isGroupChat = t.kind === "group" && t.subjectType === "none";
        const otherUid = isDmRow
          ? (t.participants || []).find((p) => p !== myUid) || null
          : null;
        const other = otherUid ? usersById[otherUid] : null;
        const name = isDmRow
          ? other
            ? getUserLabel(other)
            : "Teammate"
          : t.title || (isGroupChat ? "Group" : "Discussion");
        return {
          docId: t.docId,
          kind: t.kind,
          subjectType: t.subjectType,
          subjectId: t.subjectId,
          propertyId: t.propertyId || null,
          isGroupChat,
          otherUid,
          name,
          image: isDmRow ? other?.image || null : null,
          tag: isDmRow
            ? null
            : isGroupChat
              ? "Group"
              : SUBJECT_TAG[t.subjectType] || null,
          last: t.lastMessageText || "",
          at: t.lastMessageAt || null,
          unread: isThreadUnread(t, reads, myUid),
        };
      }),
    [threads, usersById, reads, myUid],
  );

  // Client-side search over the conversation name + last message (mirrors the
  // search box every other list/table page has).
  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.last?.toLowerCase().includes(q),
    );
  }, [rows, searchText]);

  const me = usersById[myUid];
  const myName = me ? getUserLabel(me) : auth.currentUser?.email;

  const open = (row) => {
    if (row.kind === "dm") {
      if (!row.otherUid) return;
      setOpenThread({ kind: "dm", otherUid: row.otherUid, name: row.name });
    } else if (row.isGroupChat) {
      setOpenThread({
        kind: "group",
        subjectType: "none",
        threadDocId: row.docId,
        name: row.name,
      });
    } else {
      setOpenThread({
        kind: "group",
        subjectType: row.subjectType,
        subjectId: row.subjectId,
        propertyId: row.propertyId,
        name: row.name,
      });
    }
  };

  // Deep-link from a discussion notification (/messages?thread={discussionId}) —
  // open that thread's drawer once it appears in the loaded list, then drop the
  // param so it doesn't reopen on later renders. If the thread isn't in the list
  // yet (e.g. not a participant), nothing opens — the param simply lingers.
  useEffect(() => {
    if (!deepLinkThread || !rows.length) return;
    const row = rows.find((r) => r.docId === deepLinkThread);
    if (!row) return;
    open(row);
    setSearchParams({}, { replace: true });
    // `open`/`rows` are recreated each render; key the run off the param + loaded
    // rows and clear the param on success so this opens exactly once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkThread, rows]);

  const peopleOptions = useMemo(
    () =>
      users
        .filter((u) => u.id !== myUid && !u.isDeleted && !u.isBlocked)
        .map((u) => ({ value: u.id, label: getUserLabel(u) })),
    [users, myUid],
  );

  const resetNew = () => {
    setNewOpen(false);
    setNewKind("dm");
    setDmUid(null);
    setGroupUids([]);
    setGroupTitle("");
  };

  const submitNew = async () => {
    setCreating(true);
    try {
      if (newKind === "dm") {
        if (!dmUid) return;
        const u = usersById[dmUid];
        setOpenThread({ kind: "dm", otherUid: dmUid, name: u ? getUserLabel(u) : "Teammate" });
        resetNew();
      } else {
        if (groupUids.length === 0) {
          antdMessage.warning("Pick at least one person");
          return;
        }
        const g = await createGroup({
          title: groupTitle,
          participants: groupUids,
          creatorName: myName,
        });
        setOpenThread({
          kind: "group",
          subjectType: "none",
          threadDocId: g.docId,
          name: g.title,
        });
        resetNew();
      }
    } catch (e) {
      console.error("new conversation failed", e);
      antdMessage.error(e?.message || "Couldn't start conversation");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="admin-products-main-container">
      <PageHeader
        eyebrow="Messages"
        title="Conversations"
        subtitle="Your DMs, group chats, and the task / inspection threads you're part of."
        actions={
          <>
            <Input
              className="sh-search"
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Search conversations"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <Button type="primary" onClick={() => setNewOpen(true)}>
              ＋ New conversation
            </Button>
          </>
        }
      />
      <div className="server-roles-tb-main-container">
        {filteredRows.length === 0 ? (
          <Empty
            description={
              searchText
                ? "No conversations match your search."
                : "No conversations yet. Use “New conversation” to start one."
            }
          />
        ) : (
          <List
            className="messages-list"
            itemLayout="horizontal"
            dataSource={filteredRows}
            renderItem={(r) => (
              <List.Item
                style={{ padding: "12px 16px" }}
                onClick={() => open(r)}
                extra={
                  r.unread ? (
                    <span
                      style={{
                        display: "inline-block",
                        width: 10,
                        height: 10,
                        borderRadius: 5,
                        background: "#c2603d",
                      }}
                    />
                  ) : null
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      src={r.image || undefined}
                      icon={r.isGroupChat ? <TeamOutlined /> : undefined}
                      style={{ backgroundColor: r.tag ? "#8E8E93" : "#1677ff" }}
                    >
                      {!r.isGroupChat ? getInitials(r.name) : null}
                    </Avatar>
                  }
                  title={
                    <span>
                      <span style={{ fontWeight: r.unread ? 700 : 500 }}>{r.name}</span>{" "}
                      {r.tag && <Tag style={{ marginInlineEnd: 6 }}>{r.tag}</Tag>}
                      {r.at && (
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          · {dayjs(r.at).fromNow()}
                        </Typography.Text>
                      )}
                    </span>
                  }
                  description={
                    <span style={{ fontWeight: r.unread ? 600 : 400 }}>
                      {r.last || <MessageOutlined />}
                    </span>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </div>

      <Modal
        title="New conversation"
        open={newOpen}
        onOk={submitNew}
        onCancel={resetNew}
        okText="Start"
        confirmLoading={creating}
        okButtonProps={{
          disabled: newKind === "dm" ? !dmUid : groupUids.length === 0,
        }}
      >
        <Segmented
          block
          value={newKind}
          onChange={setNewKind}
          options={[
            { label: "Direct message", value: "dm" },
            { label: "Group", value: "group" },
          ]}
          style={{ marginBottom: 12 }}
        />
        {newKind === "dm" ? (
          <Select
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="Pick a person"
            optionFilterProp="label"
            value={dmUid}
            onChange={setDmUid}
            options={peopleOptions}
          />
        ) : (
          <>
            <Input
              placeholder="Group name"
              value={groupTitle}
              onChange={(e) => setGroupTitle(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <Select
              mode="multiple"
              allowClear
              style={{ width: "100%" }}
              placeholder="Add people"
              optionFilterProp="label"
              value={groupUids}
              onChange={setGroupUids}
              options={peopleOptions}
            />
          </>
        )}
      </Modal>

      <DiscussionDrawer
        open={!!openThread}
        onClose={() => setOpenThread(null)}
        kind={openThread?.kind}
        subjectType={openThread?.subjectType}
        subjectId={openThread?.subjectId}
        threadDocId={openThread?.threadDocId}
        propertyId={openThread?.propertyId}
        title={openThread?.name}
        otherUid={openThread?.otherUid}
        otherName={openThread?.name}
        authorName={myName}
      />
    </div>
  );
};

export default Messages;
